import type { EventTypeName, TimelineFilters } from './types';
import { EVENT_TYPE_CATEGORIES, EVENT_TYPES } from './types';

/** Canonical "show all activity" set — every known event type (complete enum,
 *  kept in sync with event_types.yaml by the contract test). Includes types
 *  that have no explicit UI category (they surface via the "Other activity"
 *  row). Selecting all of these == unfiltered == server receives no
 *  event_type_name params. */
export const ALL_EVENT_TYPES: EventTypeName[] = Object.values(
  EVENT_TYPES
) as EventTypeName[];

/** Event types with no explicit EVENT_TYPE_CATEGORIES mapping. Transitional
 *  bucket surfaced by the timeline filter's "Other activity" row (issue D1);
 *  categorising a type properly removes it from here automatically. */
export const OTHER_EVENT_TYPES: EventTypeName[] = ALL_EVENT_TYPES.filter(
  type =>
    !(Object.values(EVENT_TYPE_CATEGORIES).flat() as string[]).includes(type)
);

/** Communications-only default: emails + LinkedIn messages. */
export const DEFAULT_VISIBLE_EVENT_TYPES: EventTypeName[] = [
  ...EVENT_TYPE_CATEGORIES.emails,
  ...EVENT_TYPE_CATEGORIES.linkedInMessages,
] as EventTypeName[];

/**
 * Entity types whose record-view timeline opens with the email+LinkedIn default
 * filter. Every other type opens showing all activity. Edit ONLY this list to
 * change which types get the default filter.
 */
export const ENTITY_TYPES_WITH_DEFAULT_TIMELINE_FILTERS: readonly string[] = [
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
];

/**
 * Resolves the initial event-type filter for a record-view timeline.
 *
 * Precedence:
 * 1. If `perTypeEventTypes` is provided AND non-empty → return it as-is
 *    (explicit per-type override wins; e.g. `ahoy_booking_type` which sets
 *    `eventTypes: ALL_EVENT_TYPES` in the registry).
 * 2. Else if the entity type is in ENTITY_TYPES_WITH_DEFAULT_TIMELINE_FILTERS
 *    → return DEFAULT_VISIBLE_EVENT_TYPES (email + LinkedIn only).
 * 3. Else → return ALL_EVENT_TYPES.
 *
 * Contract: `undefined` OR an empty array for `perTypeEventTypes` is treated as
 * "no override" and falls through to the allowlist / all-activity logic.
 *
 * CRITICAL: the non-allowlist fallback MUST be ALL_EVENT_TYPES, never [].
 * With [], the filter popover would render every category UNCHECKED while the
 * timeline shows everything — a confusing state. With ALL_EVENT_TYPES, all
 * categories render checked and match what is shown.
 */
export function getDefaultTimelineEventTypes(
  entityType: string,
  perTypeEventTypes?: readonly (EventTypeName | string)[]
): EventTypeName[] {
  if (perTypeEventTypes && perTypeEventTypes.length > 0) {
    return [...perTypeEventTypes] as EventTypeName[];
  }
  if (ENTITY_TYPES_WITH_DEFAULT_TIMELINE_FILTERS.includes(entityType)) {
    return DEFAULT_VISIBLE_EVENT_TYPES;
  }
  return ALL_EVENT_TYPES;
}

/** Returns a fully-cleared TimelineFilters object with all event types visible. */
export function buildClearedTimelineFilters(
  defaultIncludeAssociated?: boolean
): TimelineFilters {
  return {
    eventTypes: ALL_EVENT_TYPES,
    startDate: null,
    endDate: null,
    actorId: null,
    includeAssociated: defaultIncludeAssociated ?? false,
  };
}

/**
 * Returns true when a filter is actively restricting timeline results.
 *
 * Mirrors TimelineFilters' `activeFilterCount > 0` (TimelineFilters.tsx:143-151).
 * `includeAssociated` is intentionally excluded; an empty `eventTypes` array
 * means "show all" and is NOT an active filter.
 *
 * INVARIANT: this MUST stay consistent with TimelineFilters' activeFilterCount;
 * both route through the shared ALL_EVENT_TYPES.
 */
export function hasActiveTimelineFilters(filters: TimelineFilters): boolean {
  return (
    (filters.eventTypes.length > 0 &&
      filters.eventTypes.length < ALL_EVENT_TYPES.length) ||
    filters.startDate !== null ||
    filters.endDate !== null ||
    filters.actorId !== null
  );
}
